import {Directive, signal, inject, input, model, computed, ChangeDetectorRef, DestroyRef} from '@angular/core';
import { cloneDeep, isDefined, isEqual } from '@fi-shared/utils';
import { FiItemsListFilterHelperService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { guid } from '@fi-app/shared/utils/guid';
import { AppTextService } from '@fi-app/shared/app-text/app-text.service';
import { Subject } from 'rxjs';
import { IWidgetBase, IWidgetFilterBase } from './fi-report-widget-base.d';
import { ItemsListFilterDialogSnackbarService } from '@fi-shared/fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

@Directive()
export abstract class FiReportDetailsBaseDirective<
    TReportType,
    FilterType extends IWidgetFilterBase,
    WidgetType extends IWidgetBase<FilterType, TReportType>,
> {
    public widget = model.required<WidgetType>();
    public isFilterExpanded = model<boolean>(false);
    public reportsCount = input.required<number>();
    public currentFilter = model<FilterType>();

    protected isCustomWidget = computed(() => !!this.widget().Name?.length);

    protected initialFilter: FilterType;
    protected savedFilters: FilterType[] = [];
    protected selectedFilterItemsChanged$ = new Subject<void>();

    protected isFilterChanged = signal(false);
    protected isResetButtonVisible = signal(false);

    protected fiItemsListFilterHelperService = inject(FiItemsListFilterHelperService);
    protected appTextService = inject(AppTextService);
    protected destroyRef = inject(DestroyRef);
    protected itemsListFilterDialogSnackbarService = inject(ItemsListFilterDialogSnackbarService);

    protected abstract saveWidgetRequest(widget?: IWidgetBase<FilterType, TReportType>): Promise<number>;
    protected abstract resetReportContainer(): void;
    protected abstract getReportWidgetFilter(): FilterType;
    protected abstract isFilterEmpty(): boolean;
    protected abstract skipInitialFilterSetIfNotEmpty: boolean;

    public ngOnInit(): void {
        this.initFilterData();
        this.setSavedFilters();
    }

    protected saveFilter(): void {
        if (this.isCustomWidget()) {
            this.updateWidget();
        } else {
            this.saveNewFilter();
        }
    }

    private updateWidgetCurrentFilter(): void {
        this.widget.update((widget) => ({
            ...widget,
            Settings: {
                ...widget.Settings,
                CurrentFilterGuid: this.currentFilter().Guid,
            },
        }));
    }

    private updateWidgetFilters(filters: FilterType[]): void {
        this.widget.update((widget) => ({
            ...widget,
            Settings: {
                ...widget.Settings,
                Filters: filters,
            },
        }));
    }

    private updateWidget(): void {
        this.itemsListFilterDialogSnackbarService.getConfirmWidgetUpdateDialog().onYesButtonClick(() => {
            this.updateWidgetCurrentFilter();
            this.updateWidgetFilters([this.currentFilter()]);
            this.saveWidgetRequest().then(() => {
                this.itemsListFilterDialogSnackbarService.showWidgetUpdatedSnackbar();
                this.initFilterData();
                this.updateFilterChangedStatus();
                this.resetReportContainer();
            });
        });
    }

    private saveNewFilter(): void {
        this.itemsListFilterDialogSnackbarService.getSaveFilterDialog('').onYesButtonClick((filterName: string) => {
            const filterToSave = cloneDeep(this.currentFilter());
            filterToSave.Guid = guid();
            filterToSave.Name = filterName;
            this.updateWidgetFilters([...this.widget().Settings.Filters, filterToSave]);
            this.saveWidgetRequest().then((widgetId) => {
                this.itemsListFilterDialogSnackbarService.showFilterSavedSnackbar(filterName);
            });
            this.currentFilter.set(cloneDeep(this.getWidgetFilterByGuid(filterToSave.Guid)));
            this.setSavedFilters();
            this.isFilterChanged.set(false);
            this.isResetButtonVisible.set(true);
            this.resetReportContainer();
            // this.cdr.markForCheck();
        });
    }

    protected saveNewWidget(): void {
        this.itemsListFilterDialogSnackbarService
            .getSaveFilterAsWidgetDialog(
                this.appTextService.get('checklist.filter_save_new_widget'),
                this.appTextService.get('checklist.filter_save_new_widget_description'),
            )
            .onYesButtonClick((widgetName: string) => {
                const filterToSave = cloneDeep(this.currentFilter());
                if (!isDefined(filterToSave.Guid)) {
                    filterToSave.Guid = guid();
                }

                const newReportWidget: IWidgetBase<FilterType, TReportType> = {
                    Name: widgetName,
                    Description: '',
                    ReportType: this.widget().ReportType,
                    SortIndex: this.reportsCount(),
                    Settings: {
                        CurrentFilterGuid: filterToSave.Guid,
                        Filters: [filterToSave],
                    },
                };
                this.saveWidgetRequest(newReportWidget).then((widgetId) => {
                    this.itemsListFilterDialogSnackbarService.showWidgetAddedSnackbar();
                    this.resetReportContainer();
                });
            });
    }

    private setSavedFilters(): void {
        this.savedFilters = this.widget().Settings.Filters?.filter((filter) => isDefined(filter.Name)) || [];
    }

    private getWidgetFilterByGuid(guid: string): FilterType {
        return this.widget().Settings.Filters.find((filter) => filter.Guid === guid);
    }

    private getWidgetFilterIndexByGuid(guid: string): number {
        return this.widget().Settings.Filters.findIndex((filter) => filter.Guid === guid);
    }

    protected onSavedFiltersChange(savedFilters: FilterType[]): void {
        this.updateWidgetFilters(savedFilters);
        if (!!this.currentFilter()?.Guid?.length && !this.isFilterChanged()) {
            const savedFilter = this.getWidgetFilterByGuid(this.currentFilter().Guid);
            if (isDefined(savedFilter)) {
                this.currentFilter.set(cloneDeep(savedFilter));
                this.onSelectedItemsChanged();
                this.isFilterChanged.set(false);
                this.isResetButtonVisible.set(false);
            } else {
                this.isFilterChanged.set(true);
                this.isResetButtonVisible.set(true);
            }
        }
        this.setSavedFilters();
        this.saveWidgetRequest().then((widgetId) => {
            this.resetReportContainer();

            // show snackbar based on action? deleted/renamed/updated
        });
    }

    private updateWidgetFilterByGuid(guid: string, filter: FilterType): void {
        const filterIndex: number = this.getWidgetFilterIndexByGuid(guid);
        const newWidgetFilters = [
            ...this.widget().Settings.Filters.slice(0, filterIndex),
            filter,
            ...this.widget().Settings.Filters.slice(filterIndex + 1),
        ];
        this.updateWidgetFilters(newWidgetFilters);
    }

    protected updateCurrentFilter(): void {
        const filterName = this.currentFilter().Name;
        const filterGuid = this.currentFilter().Guid;
        this.updateWidgetFilterByGuid(filterGuid, this.currentFilter());
        this.saveWidgetRequest().then(() => {
            this.itemsListFilterDialogSnackbarService.showFilterUpdatedSnackbar(filterName);
            this.currentFilter.set(cloneDeep(this.getWidgetFilterByGuid(filterGuid)));
            this.isFilterChanged.set(false);
            this.resetReportContainer();
        });
    }

    protected onSelectedItemsChanged(): void {
        this.selectedFilterItemsChanged$.next();
        this.updateFilterChangedStatus();
    }

    protected onSelectFilter(filterGuid: string): void {
        this.currentFilter.set(cloneDeep(this.getWidgetFilterByGuid(filterGuid)));
        this.onSelectedItemsChanged();
        this.isFilterChanged.set(false);
        this.isResetButtonVisible.set(true);
    }

    protected resetCurrentFilter(): void {
        this.currentFilter.set(cloneDeep(this.initialFilter));
        this.onSelectedItemsChanged();
    }

    protected updateFilterChangedStatus(): void {
        const isFilterChanged = !isEqual(this.currentFilter(), this.initialFilter);
        this.isFilterChanged.set(isFilterChanged);
        this.isResetButtonVisible.set(isFilterChanged);
    }

    protected toggleFilterVisibility(): void {
        this.isFilterExpanded.update((value) => !value);
    }

    private initFilterData(): void {
        if (!(this.skipInitialFilterSetIfNotEmpty && !this.isFilterEmpty())) {
            this.currentFilter.set(cloneDeep(this.getReportWidgetFilter()));
        }
        this.initialFilter = cloneDeep(this.currentFilter());
        this.isFilterChanged.set(false);
        this.isResetButtonVisible.set(false);
    }

    protected isSaveFilterButtonsVisible(isFilterChanged: boolean): boolean {
        return this.isCustomWidget() ? !this.isFilterEqualsToInitial() && !this.isFilterEmpty() : isFilterChanged;
    }

    private isFilterEqualsToInitial(): boolean {
        const initialFilterCopy: FilterType = cloneDeep(this.initialFilter);
        delete initialFilterCopy.Name;
        delete initialFilterCopy.Guid;
        const currentFilterCopy: FilterType = cloneDeep(this.currentFilter());
        delete currentFilterCopy.Name;
        delete currentFilterCopy.Guid;
        return isEqual(currentFilterCopy, initialFilterCopy);
    }
}
